import { useCallback, useEffect, useState } from 'react';
import {
  Colors,
  Flex,
  IconClose,
  PrimaryButton,
  RemoteImage,
  Typography,
} from '@banhala/ui-kit';
import type { TasteOnboardingCardProps } from './types';
import { useOpenDeeplink } from '../../../../hooks';
import { useLogAnalyticsEvent } from '../../../../hooks/useLogAnalyticsEvent';

export const TasteOnboardingCard = ({
  entity,
  logging,
}: TasteOnboardingCardProps) => {
  const { openDeeplink } = useOpenDeeplink();
  const [dismissed, setDismissed] = useState(false);
  const item = entity?.[0];

  const logAnalyticsEvent = useLogAnalyticsEvent();

  useEffect(() => {
    if (!item) return;
    logAnalyticsEvent({
      name: 'DISPLAY_ONBOARDING',
      analytics: { ...logging?.analytics, ...item.logging?.analytics },
      ably_only_log: { ...logging?.ably_only_log, ...item.logging?.ably_only_log },
    });
  }, [item, logging, logAnalyticsEvent]);

  const handlePressButton = useCallback(() => {
    if (!item) return;
    logAnalyticsEvent({
      name: 'CLICK_ONBOARDING',
      analytics: { ...logging?.analytics, ...item.logging?.analytics },
      ably_only_log: { ...logging?.ably_only_log, ...item.logging?.ably_only_log },
    });
    openDeeplink(item.item.button.deeplink);
  }, [item, logging, openDeeplink, logAnalyticsEvent]);

  const handleClose = useCallback(() => {
    setDismissed(true);
  }, []);

  if (!item || dismissed) return null;

  const { images, title, description, button } = item.item;

  return (
    <Flex margin={{ horizontal: 12, bottom: 16 }}>
      <Flex
        backgroundColor="bg_secondary"
        borderRadius={8}
        padding={{ top: 32, bottom: 20, horizontal: 16 }}
        alignItems="center"
      >
        {/* 닫기 버튼 */}
        <Flex
          position="absolute"
          top={8}
          right={8}
          width={32}
          height={32}
          alignItems="center"
          justifyContent="center"
          onPress={handleClose}
        >
          <IconClose size={20} fill={Colors.content_secondary} />
        </Flex>

        {/* 이미지 overlapping */}
        {images.length > 0 && (
          <Flex.Row alignItems="center" justifyContent="center" margin={{ bottom: 12 }}>
            {images.map((img, index) => {
              const middleIndex = Math.floor(images.length / 2);
              const isCenter = index === middleIndex;
              const size = isCenter ? 64 : 48;
              const distanceFromMiddle = Math.abs(middleIndex - index);
              return (
                <Flex
                  key={img}
                  width={size}
                  height={size}
                  borderRadius={size / 2}
                  overflow="hidden"
                  borderWidth={2}
                  borderColor="bg_secondary"
                  margin={{ left: index > 0 ? -8 : 0 }}
                  zIndex={images.length - distanceFromMiddle}
                >
                  <RemoteImage uri={img} width={size} height={size} resizeMode="cover" />
                </Flex>
              );
            })}
          </Flex.Row>
        )}

        <Typography type="subtitle1" color="content_primary" textAlign="center">
          {title}
        </Typography>
        <Flex margin={{ top: 4, bottom: 12 }}>
          <Typography type="body4" color="content_secondary" textAlign="center">
            {description}
          </Typography>
        </Flex>

        <Flex alignSelf="center">
          <PrimaryButton size="small" color="gray" onPress={handlePressButton}>
            {button.title}
          </PrimaryButton>
        </Flex>
      </Flex>
    </Flex>
  );
};
